/**
 * O catálogo de tipos, em runtime.
 *
 * É o único lugar do sistema que sabe quais tipos existem. Paleta, canvas,
 * inspector, validação e formulário consultam daqui — nenhum deles carrega a
 * lista por conta própria, como acontecia no modelo antigo.
 */
import { LayoutSpec } from '../model/layout';
import { NodeBase, NodeType, ScreenNode, isNodeType } from '../model/node-props';
import {
  ComponentCategory,
  ComponentDescriptor,
  CreateContext
} from './component-descriptor';
import { PropertyLevel } from './property-schema';

import { ADVANCED_DESCRIPTORS } from './builtin/advanced.descriptors';
import { CONTAINER_DESCRIPTORS } from './builtin/containers.descriptors';
import { DASHBOARD_DESCRIPTORS } from './builtin/dashboard.descriptors';
import { DISPLAY_AND_DATA_DESCRIPTORS } from './builtin/display-and-data.descriptors';
import { FIELD_DESCRIPTORS } from './builtin/field.descriptors';

/** Todos os descriptors nativos, na ordem em que aparecem na paleta. */
export const BUILTIN_DESCRIPTORS: readonly ComponentDescriptor[] = [
  ...CONTAINER_DESCRIPTORS,
  ...FIELD_DESCRIPTORS,
  ...DISPLAY_AND_DATA_DESCRIPTORS,
  ...ADVANCED_DESCRIPTORS,
  ...DASHBOARD_DESCRIPTORS
];

/** Grupo da paleta: uma categoria e os tipos dela. */
export interface PaletteGroup {
  readonly category: ComponentCategory;
  readonly descriptors: readonly ComponentDescriptor[];
}

/**
 * Consulta ao catálogo.
 *
 * Sem decorador do Angular de propósito: é TypeScript puro, testável sem TestBed.
 * O editor registra como provider através de um `InjectionToken` próprio.
 */
export class ScreenComponentRegistry {
  private readonly byType = new Map<NodeType, ComponentDescriptor>();

  constructor(descriptors: readonly ComponentDescriptor[] = BUILTIN_DESCRIPTORS) {
    for (const descriptor of descriptors) {
      if (this.byType.has(descriptor.type)) {
        throw new Error(
          `ScreenComponentRegistry: já existe descriptor para o tipo "${descriptor.type}"`
        );
      }
      this.byType.set(descriptor.type, descriptor);
    }
  }

  // ------------------------------------------------------------ consulta

  has(type: string): type is NodeType {
    return isNodeType(type) && this.byType.has(type);
  }

  /** Descriptor do tipo. Lança se não existir — tipo desconhecido é erro de programação. */
  get<K extends NodeType>(type: K): ComponentDescriptor<K> {
    const descriptor = this.byType.get(type);
    if (!descriptor) {
      throw new Error(`ScreenComponentRegistry: nenhum descriptor para o tipo "${type}"`);
    }
    return descriptor as ComponentDescriptor<K>;
  }

  /** Descriptor do tipo, ou `undefined`. Para quando o tipo veio de dado externo. */
  find(type: string): ComponentDescriptor | undefined {
    return isNodeType(type) ? this.byType.get(type) : undefined;
  }

  all(): readonly ComponentDescriptor[] {
    return [...this.byType.values()];
  }

  // ------------------------------------------------------------ paleta

  /**
   * Tipos disponíveis na paleta, agrupados por categoria e filtrados pelo nível.
   *
   * No modo simples ficam ~10 itens com nome de negócio; no avançado, o catálogo
   * inteiro. Containers estruturais que o usuário não arrasta (`screen`, `tab`)
   * ficam de fora dos dois.
   */
  buildPalette(level: PropertyLevel): readonly PaletteGroup[] {
    // A raiz e a tela, e nao ha onde inserir outra.
    //
    // A `tab` tambem fica fora, e a tentativa de coloca-la aqui e o registro de
    // por que: o catalogo passou a mostrar "Abas" (o container) e "Aba" (uma
    // aba) lado a lado, na mesma categoria, com uma letra de diferenca. O
    // usuario clicava no que via e ganhava um container de abas NOVO na raiz.
    // Dois itens quase iguais nao sao catalogo, sao armadilha.
    //
    // Acrescentar aba e o "+" na propria barra de abas — onde qualquer pessoa
    // procura, sem homonimo, e so onde faz sentido. Ver `requestInsert` no
    // contrato do EDITOR_CHROME.
    const NOT_IN_PALETTE: readonly NodeType[] = ['screen', 'tab'];

    const visible = this.all().filter(descriptor => {
      if (NOT_IN_PALETTE.includes(descriptor.type)) {
        return false;
      }
      if (level === 'simple' && descriptor.level === 'advanced') {
        return false;
      }
      return true;
    });

    const order: readonly ComponentCategory[] = [
      'input', 'selection', 'data', 'layout', 'action', 'display'
    ];

    const groups: PaletteGroup[] = [];
    for (const category of order) {
      const descriptors = visible.filter(descriptor => descriptor.category === category);
      if (descriptors.length > 0) {
        groups.push({ category, descriptors });
      }
    }
    return groups;
  }

  // ------------------------------------------------------------ criação

  /**
   * Cria um nó novo a partir do descriptor.
   * Substitui o `createDefaultProps`, que era um `switch` de ~900 linhas.
   */
  createNode<K extends NodeType>(type: K, context: CreateContext): ScreenNode<K> {
    const descriptor = this.get(type);
    const created = descriptor.createDefault(context);

    const layout: LayoutSpec = {
      order: context.order,
      ...created.layout
    };

    // Montado por partes, em vez de um objeto solto convertido no fim: assim o
    // TypeScript confere props contra o tipo, e `children` só aparece quando existe
    // (chave com undefined some no JSON.stringify e quebraria o round-trip).
    const base = {
      id: context.id,
      type,
      props: created.props,
      layout
    } satisfies Pick<NodeBase<K>, 'id' | 'type' | 'props' | 'layout'>;

    if (created.children === undefined) {
      return base as ScreenNode<K>;
    }

    return { ...base, children: created.children } as ScreenNode<K>;
  }
}

/** Instância única para uso fora do Angular (testes, scripts, migração). */
export const defaultRegistry = new ScreenComponentRegistry();
